import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, AppRole } from '../../context/AuthContext';
import { hasCompletedOnboarding } from '../../app/onboarding/OnboardingPage';

interface Props {
  children: React.ReactNode;
  /** If provided, user must have one of these roles. Otherwise any authenticated user passes. */
  roles?: AppRole[];
  /** Skip the onboarding-redirect check — used on the onboarding route itself. */
  skipOnboardingCheck?: boolean;
}

export default function ProtectedRoute({ children, roles, skipOnboardingCheck }: Props) {
  // Use the canonical `role` from AuthContext (Clerk publicMetadata first,
  // Supabase fallback). Reading profile.role directly was stale because the
  // /auth/me lookup falls back to viewer when the JWT has no role embedded
  // and user_profiles RLS errors.
  const { user, profile, role, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-400 text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // First-visit gate: redirect new authenticated users to /onboarding
  // unless they're already on it (skipOnboardingCheck) or they've
  // completed it. Using localStorage means the gate is per-device and
  // disappears the moment they finish or click "Skip tour".
  if (!skipOnboardingCheck && !hasCompletedOnboarding() && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  if (profile?.status === 'suspended') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="bg-red-900/40 border border-red-700 text-red-200 rounded-xl p-6 max-w-sm text-center">
          <div className="text-3xl mb-3">🚫</div>
          <h2 className="font-semibold text-lg mb-1">Account suspended</h2>
          <p className="text-sm text-red-300">
            Your account has been suspended. Contact your administrator for assistance.
          </p>
        </div>
      </div>
    );
  }

  if (profile?.status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="bg-yellow-900/40 border border-yellow-700 text-yellow-200 rounded-xl p-6 max-w-sm text-center">
          <div className="text-3xl mb-3">⏳</div>
          <h2 className="font-semibold text-lg mb-1">Approval pending</h2>
          <p className="text-sm text-yellow-300">
            Your account is awaiting administrator approval. You'll be notified by email once approved.
          </p>
        </div>
      </div>
    );
  }

  // Require the role to be present AND in the allowed list. A null role
  // (Clerk publicMetadata empty + Supabase profile missing) is treated as
  // not-permitted for any role-gated route.
  if (roles && (!role || !roles.includes(role))) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="bg-gray-900 border border-gray-700 text-gray-200 rounded-xl p-6 max-w-sm text-center">
          <div className="text-3xl mb-3">🔒</div>
          <h2 className="font-semibold text-lg mb-1">Access restricted</h2>
          <p className="text-sm text-gray-400">
            You don't have permission to view this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm text-white transition-colors"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
